/**
 * Community Goal Store
 *
 * Zustand store for community goals state management.
 */

import { createWithEqualityFn } from 'zustand/traditional'
import { shallow } from 'zustand/shallow'
import { communityGoalApi } from '../api'
import type {
  CommunityGoal,
  GoalContribution,
  GoalParticipation,
  GoalLeaderboardEntry,
  UUID,
} from '../types'

interface CommunityGoalState {
  // State
  goals: CommunityGoal[]
  activeGoals: CommunityGoal[]
  selectedGoal: CommunityGoal | null
  participation: GoalParticipation | null
  leaderboard: GoalLeaderboardEntry[]
  recentContributions: GoalContribution[]
  isLoading: boolean
  error: string | null

  // Actions
  createGoal: (communityId: UUID, data: {
    title: string
    description: string
    contributionType: 'challenges_completed' | 'xp_earned' | 'hours_spent' | 'custom'
    targetAmount: number
    startDate: string
    endDate: string
    rewardXp: number
  }) => Promise<boolean>
  fetchGoals: (communityId: UUID) => Promise<void>
  fetchActiveGoals: () => Promise<void>
  fetchGoalById: (goalId: UUID) => Promise<void>
  joinGoal: (goalId: UUID) => Promise<boolean>
  contribute: (
    goalId: UUID,
    amount: number,
    challengeId?: UUID,
    trackRecordId?: UUID
  ) => Promise<boolean>
  fetchLeaderboard: (goalId: UUID) => Promise<void>
  fetchMyParticipation: (goalId: UUID) => Promise<void>
  fetchRecentContributions: (goalId: UUID) => Promise<void>
  clearSelectedGoal: () => void
  clearError: () => void
}

export const useCommunityGoalStore = createWithEqualityFn<CommunityGoalState>((set, get) => ({
  // Initial state
  goals: [],
  activeGoals: [],
  selectedGoal: null,
  participation: null,
  leaderboard: [],
  recentContributions: [],
  isLoading: false,
  error: null,

  // Create a new goal
  createGoal: async (communityId, data) => {
    set({ isLoading: true, error: null })
    const response = await communityGoalApi.createGoal(communityId, data)
    if (response.success) {
      set((state) => ({ goals: [response.data, ...state.goals], isLoading: false }))
      return true
    } else {
      set({ error: response.error, isLoading: false })
      return false
    }
  },

  // Fetch goals for a community
  fetchGoals: async (communityId) => {
    set({ isLoading: true, error: null })
    const response = await communityGoalApi.getGoals(communityId)
    if (response.success) {
      set({ goals: response.data, isLoading: false })
    } else {
      set({ error: response.error, isLoading: false })
    }
  },

  // Fetch all active goals
  fetchActiveGoals: async () => {
    set({ isLoading: true, error: null })
    const response = await communityGoalApi.getActiveGoals()
    if (response.success) {
      set({ activeGoals: response.data, isLoading: false })
    } else {
      set({ error: response.error, isLoading: false })
    }
  },

  // Fetch single goal
  fetchGoalById: async (goalId) => {
    set({ isLoading: true, error: null })
    const response = await communityGoalApi.getGoalById(goalId)
    if (response.success) {
      set({ selectedGoal: response.data, isLoading: false })
      // Also fetch related data
      get().fetchLeaderboard(goalId)
      get().fetchMyParticipation(goalId)
      get().fetchRecentContributions(goalId)
    } else {
      set({ error: response.error, isLoading: false })
    }
  },

  // Join a goal
  joinGoal: async (goalId) => {
    set({ isLoading: true, error: null })
    const response = await communityGoalApi.joinGoal(goalId)
    if (response.success) {
      set({ participation: response.data, isLoading: false })
      // Refresh goal to get updated participant count
      get().fetchGoalById(goalId)
      return true
    } else {
      set({ error: response.error, isLoading: false })
      return false
    }
  },

  // Make a contribution
  contribute: async (goalId, amount, challengeId, trackRecordId) => {
    set({ isLoading: true, error: null })
    const response = await communityGoalApi.contribute(goalId, amount, challengeId, trackRecordId)
    if (response.success) {
      set({ isLoading: false })
      // Refresh goal and leaderboard
      get().fetchGoalById(goalId)
      return true
    } else {
      set({ error: response.error, isLoading: false })
      return false
    }
  },

  // Fetch leaderboard
  fetchLeaderboard: async (goalId) => {
    const response = await communityGoalApi.getLeaderboard(goalId)
    if (response.success) {
      set({ leaderboard: response.data })
    }
  },

  // Fetch user's participation
  fetchMyParticipation: async (goalId) => {
    const response = await communityGoalApi.getMyParticipation(goalId)
    if (response.success) {
      set({ participation: response.data })
    }
  },

  // Fetch recent contributions
  fetchRecentContributions: async (goalId) => {
    const response = await communityGoalApi.getRecentContributions(goalId)
    if (response.success) {
      set({ recentContributions: response.data })
    }
  },

  // Clear selected goal
  clearSelectedGoal: () => {
    set({
      selectedGoal: null,
      participation: null,
      leaderboard: [],
      recentContributions: [],
    })
  },

  // Clear error
  clearError: () => {
    set({ error: null })
  },
}))
